import { PixelRatio } from 'react-native';

/**
 * Web-specific override for font scaling utilities.
 *
 * On web the window dimensions are already CSS pixels (no pixel-ratio scaling),
 * and the window *width* can be very large on desktop monitors. Using
 * Math.max(width, height) as the reference height — as the native version does —
 * causes fonts to blow up proportionally with the viewport.
 *
 * This override returns deterministic values that match the documented "Result"
 * comments in Typography.ts (base * 2), regardless of the browser window size.
 *
 *   RFValue(5)  → 10   (Typography.fontSize.fontSize10)
 *   RFValue(8)  → 16   (Typography.fontSize.fontSize16)
 *   RFValue(12) → 24   (Typography.fontSize.fontSize24)
 */

/**
 * Web version: returns fontSize * 2 — a fixed CSS-pixel value that is
 * independent of the browser window size.
 */
export const RFValue = (fontSize: number, _standardScreenHeight = 680): number => {
  return Math.round(fontSize * 2);
};

/**
 * Web version: same fixed-pixel approach as RFValue.
 */
export const RFPercentage = (percent: number): number => {
  // Treat as if the device height is 680px (the standard reference height).
  return Math.round((percent * 680) / 100);
};

/**
 * Web version: no-op scaling — returns the raw value directly.
 */
export const getScaledFontSize = (defaultSize: number): number => {
  return defaultSize;
};

/**
 * Web version: width-based scaling is not meaningful on web
 * — return the fixed pixel value.
 */
export const RFValueWidth = (fontSize: number, _standardScreenWidth = 375): number => {
  return Math.round(fontSize * 2);
};

/**
 * Web version: same as RFValue.
 */
export const RFDynamic = (fontSize: number, _standardScreenHeight = 680): number => {
  return Math.round(fontSize * 2);
};

// Predefined font sizes (web-safe, fixed CSS-pixel values)
export const FontSizes = {
  tiny: RFValue(10),
  small: RFValue(12),
  regular: RFValue(14),
  medium: RFValue(16),
  large: RFValue(18),
  xlarge: RFValue(20),
  xxlarge: RFValue(24),
  huge: RFValue(32),
};

export default {
  RFPercentage,
  RFValue,
  RFValueWidth,
  RFDynamic,
  FontSizes,
  getScaledFontSize,
};